import { User } from "oidc-client-ts";

/** A token this close to its expiry is renewed before it is sent. */
const RENEW_MARGIN_SECONDS = 5;

/**
 * How long a caller waits for a renewal. oidc-client-ts gives up a silent
 * request after 10 s by default; this cap only guards against a renewal that
 * never settles.
 */
const RENEWAL_TIMEOUT_MS = 15_000;

export interface TokenRenewalOptions {
  /** Renews the stored user silently (`useAuth().signinSilent`). */
  signinSilent: () => Promise<User | null>;
  /**
   * The navigator under way (`useAuth().activeNavigator`). A sign-in or
   * sign-out is not interrupted: nothing is renewed. `signinSilent` is only
   * ever this getter's own renewal, which `renew` already deduplicates.
   */
  getNavigator?: () => string | undefined;
}

export type GetAuthToken = (() => Promise<string | null>) & {
  /**
   * Renews the token, or joins the renewal under way. Resolves with the
   * renewed user, or with null when the renewal failed, timed out or could
   * not start.
   */
  renew: () => Promise<User | null>;
};

/**
 * The token getter of the OIDC login, shared by the HTTP and WebSocket links.
 *
 * Returns the stored access token. A token that has expired or expires within
 * five seconds is renewed first: all callers wait for one shared renewal and
 * get the new token. When the renewal fails or takes longer than 15 s, they get
 * the stored token, so the server rejects it and the session-rejection flow
 * takes over. Without `renewal` the stored token is returned as it is.
 */
export function makeGetAuthToken(
  authority: string,
  clientId: string,
  renewal?: TokenRenewalOptions,
): GetAuthToken {
  let current: Promise<User | null> | null = null;

  function getUser() {
    const oidcStorage = localStorage.getItem(
      `oidc.user:${authority}:${clientId}`,
    );
    if (!oidcStorage) {
      return null;
    }

    return User.fromStorageString(oidcStorage);
  }

  function renew(): Promise<User | null> {
    if (current !== null) return current;
    const navigator = renewal?.getNavigator?.();
    if (!renewal || (navigator && navigator !== "signinSilent")) {
      return Promise.resolve(null);
    }
    const started = runRenewal(renewal.signinSilent).finally(() => {
      if (current === started) current = null;
    });
    current = started;
    return started;
  }

  const getAuthToken = async (): Promise<string | null> => {
    const user = getUser();
    const token = user?.access_token;
    if (!user || !token) {
      return null;
    }
    if (!needsRenewal(user)) {
      return token;
    }
    const renewed = await renew();
    return renewed?.access_token ?? token;
  };

  return Object.assign(getAuthToken, { renew });
}

function needsRenewal(user: User): boolean {
  const expiresIn = user.expires_in;
  return expiresIn !== undefined && expiresIn <= RENEW_MARGIN_SECONDS;
}

/** Runs one silent renewal. Settles with null on failure or timeout. */
function runRenewal(
  signinSilent: TokenRenewalOptions["signinSilent"],
): Promise<User | null> {
  return new Promise<User | null>((resolve) => {
    const timeout = setTimeout(() => resolve(null), RENEWAL_TIMEOUT_MS);
    signinSilent()
      .then(resolve, (error: unknown) => {
        console.error("access token renew failed", error);
        resolve(null);
      })
      .finally(() => clearTimeout(timeout));
  });
}
